import { buffers, eventChannel, END, EventChannel } from 'redux-saga';
import { delay, select, take, put, call, all, takeEvery, takeLatest } from 'redux-saga/effects';
import { makeNavigateToBuildingRoute } from './makeNavigateToBuildingRoute';
import { push as navigateTo, replace } from 'connected-react-router';
import { getBuilding } from 'selectors/buildingsSelectors';
import { getEditorTheme, getEditorBuildingId, isEditorDirty, getActiveAsset, getEditorExitPath } from 'selectors/editorSelectors';
import { getTheme } from 'selectors/themesSelectors';
import {
    Actions, saveThemeSuccess, saveThemeFailure, setAssetSize,
    uploadAssetProgress, uploadAssetSuccess, uploadAssetFailure,
    loadNewsSuccess, loadNewsFailure,
    clearError, clearUploadAssetProgress,
    loadWeatherSuccess, loadWeatherFailure, loadStocksSuccess, loadStocksFailure, editTheme, loadNewsRequest, loadWeatherRequest, hideEditorRequest,
} from 'actions/editorActions';
import { apiGet, apiPost, apiPut } from './apiSagas';
import { confirmSaga } from './confirmSaga';
import i18n from 'i18next';
import { contentUrl } from 'util/env';
import { Asset, Building, BuildingId, Theme } from 'shared/models';
import { toError } from 'shared/util/toError';

//import { actionTypes as ReduxFormActions } from 'redux-form';
const ReduxFormActions = require('redux-form').actionTypes;

const navigateToBuildingThemes = makeNavigateToBuildingRoute("themes");

function* saveEditorThemeSaga(): Generator<any, boolean, any> {
    const buildingId: BuildingId = yield select(getEditorBuildingId);
    const editorTheme: Theme = yield select(getEditorTheme);
    const { id, lastSaved } = editorTheme;

    try {
        const [ theme ] = yield all([
            call(apiPut, `/api/themes/${id}`, { ...editorTheme, buildingId }),
            delay(1000), // Show the saving animation for at least one second
        ]);
        yield put(saveThemeSuccess({ theme, buildingId }));

        // If we just saved a new theme, change the URL to refer to the
        // theme by GUID instead of 'new'
        if (!lastSaved) {
            const building: Building = yield select(getBuilding, { buildingId });
            const { urlSlug } = building;
            yield put(replace(`/buildings/${urlSlug}/themes/${id}/edit`));
        }

        return true;
    }
    catch (e) {
        const err = toError(e);
        const errorString = i18n.t([ `THEMES.ERROR.${err.message}`, 'SERVER_ERROR' ]);
        yield put(saveThemeFailure(errorString));
        return false;
    }
}

export function* saveEditorThemesSaga() {
    yield takeEvery(Actions.SAVE_THEME_REQUEST, saveEditorThemeSaga);
}

export function* showEditorSaga() {
    yield takeEvery(Actions.SHOW_EDITOR, function*(action: any) {
        const { themeId, buildingId } = action.payload;
        const theme: Theme = yield select(getTheme, { themeId });
        if (theme) {
            yield put(editTheme(theme, { buildingId }));
        }
    });
}

export function* closeEditorSaga() {
    while (true) {
        const action: ReturnType<typeof hideEditorRequest> = yield take(Actions.HIDE_EDITOR_REQUEST);

        const hasUnsavedChanges: boolean = yield select(isEditorDirty);
        if (hasUnsavedChanges) {
            const wantToSave: boolean = yield call(confirmSaga, i18n.t('THEME_EDITOR.SAVE_DISCARD_PROMPT'));
            if (wantToSave) {
                const wasSaved: boolean = yield call(saveEditorThemeSaga);
                if (!wasSaved) {
                    continue;
                }
            }
        }

        const buildingId: BuildingId = yield select(getEditorBuildingId);

        const redirectPath: string = yield select(getEditorExitPath);

        if (redirectPath) {
            yield put(navigateTo(redirectPath));
        }
        else {
            yield call(navigateToBuildingThemes, buildingId, '/buildings');
        }
    }
}

// Remove the error after the user has dismissed it and the close-window
// animation has had time to run. We do this because removing the error
// right away causes the window text to disappear before the window closes.
export function* clearErrorSaga() {
    while (true) {
        yield take(Actions.DISMISS_ERROR);
        yield delay(400);
        yield put(clearError());
    }
}

function createUploadFileChannel(endpoint: string, file: File) {
    return eventChannel(emitter => {
        const xhr = new XMLHttpRequest();

        const onProgress = (e: ProgressEvent) => {
            if (e.lengthComputable) {
                const progress = e.loaded / e.total;
                console.log(`upload progress ${progress}`);
                emitter({ progress });
            }
        };
        const onFailure = (e: ProgressEvent) => {
            emitter({ error: 'THEME_EDITOR.ERROR.UPLOAD_FAILED' });
            emitter(END);
        };

        // Delay starting the upload to give the opening animation (400ms) time to run
        setTimeout(() => {
            xhr.upload.addEventListener("progress", onProgress);
            xhr.upload.addEventListener("error", onFailure);
            xhr.upload.addEventListener("abort", onFailure);
            xhr.onreadystatechange = e => {
                const { readyState, status } = xhr;
                if (readyState === 4) {
                    if (status === 200) {
                        console.log('upload complete');

                        // Advance the progress bar to 100%
                        emitter({ progress: 1 });

                        // Delay the actual acknowledgement of completion so the
                        // user has time to visually process what just happened.
                        setTimeout(() => {
                            emitter({ success: true });
                            emitter(END);
                        }, 1000);
                    }
                    else {
                        emitter({ error: 'THEME_EDITOR.ERROR.UPLOAD_FAILED' });
                        emitter(END);
                    }
                }
            };

            xhr.open("PUT", endpoint, true);
            xhr.setRequestHeader("Content-Type", file.type);
            xhr.setRequestHeader("Cache-Control", "max-age=7948800"); // Approximately three months
            xhr.send(file);
        }, 400);

        return () => {
            xhr.upload.removeEventListener("progress", onProgress);
            xhr.upload.removeEventListener("error", onFailure);
            xhr.upload.removeEventListener("abort", onFailure);
            xhr.onreadystatechange = null;
            xhr.abort();
        };
    }, buffers.sliding<object>(2));
}

const extensionFromFilename = (filename: string) => {
    if (!filename) return "dat";

    const index = filename.lastIndexOf('.');
    if (index < 0) return "dat";

    return filename.substr(index + 1);
};

function* uploadAssetSaga(themeId: string, assetId: string, file: File, width: number, height: number) {
    let url: string;
    try {
        const extension: string = yield call(extensionFromFilename, file.name);
        url = yield call(apiPost, `/api/themes/${themeId}/assets/${assetId}.${extension}`, { mimeType: file.type, width, height });
    }
    catch (e) {
        const err = toError(e);
        yield put(uploadAssetFailure(i18n.t(err.message), { assetId }));
        yield take(Actions.DISMISS_ERROR);
        yield put(clearUploadAssetProgress(assetId));
        return;
    }

    const channel: EventChannel<object> = yield call(createUploadFileChannel, url, file);
    while (true) {
        const { progress = 0, error, success } = yield take(channel);
        if (error) {
            yield put(uploadAssetFailure(i18n.t(error), { assetId }));
            yield take(Actions.DISMISS_ERROR);
            yield put(clearUploadAssetProgress(assetId));
            return;
        }
        if (success) {
            yield put(uploadAssetSuccess({ assetId, filename: file.name }));
            return;
        }

        yield put(uploadAssetProgress({ assetId, progress }));
    }
}

export function* uploadAssetsSaga() {
    yield takeEvery(Actions.UPLOAD_ASSET_REQUEST, function*(action: any) {
        if (!action) return;
        const { themeId, assetId, file, width, height } = action.payload;
        yield call(uploadAssetSaga, themeId, assetId, file, width, height);
    });
}

export function* loadNewsSaga() {
    while (true) {
        const action: ReturnType<typeof loadNewsRequest> = yield take(Actions.LOAD_NEWS_REQUEST);
        const channelId = action.payload;
        const buildingId: BuildingId = yield select(getEditorBuildingId);

        try {
            const cacheBust = ((yield call(Math.random)) as number).toString(36).substring(2);

            let news: unknown[];
            if (/^http/i.test(channelId)) {
                news = yield call(apiGet, `${contentUrl}/rss/${encodeURIComponent(channelId)}?${cacheBust}`);
            }
            else {
                news = yield call(apiGet, `${contentUrl}/news/${channelId}?buildingId=${buildingId}&${cacheBust}`);
            }
            yield put(loadNewsSuccess({ channelId, news, updated: new Date().getTime() }));
        }
        catch (e) {
            const err = toError(e);
            yield put(loadNewsFailure({ channelId, err }));
        }
    }
}

export function* loadStocksSaga() {
    yield takeEvery(Actions.LOAD_STOCKS_REQUEST, function*(action: any) {
        if (!action) return;
        const { symbols, provider = 'default' } = action.payload;
        if (!symbols) return;

        const buildingId: BuildingId = yield select(getEditorBuildingId);

        try {
            const cacheBust = ((yield call(Math.random)) as number).toString(36).substring(2);
            const stocks: unknown = yield call(apiGet, `${contentUrl}/stocks/${encodeURIComponent(symbols)}?buildingId=${buildingId}&provider=${provider}&${cacheBust}`);
            yield put(loadStocksSuccess({ provider, symbols, stocks, updated: new Date().getTime() }));
        }
        catch (e) {
            const err = toError(e);
            yield put(loadStocksFailure(err.message, { provider, symbols }));
        }
    });
}

//
// Watch for the themeId being set in the Theme Asset Properties Editor.
// Update the asset with the theme's width and height so that other code
// doesn't need to lookup these values. (i.e. the theme content builder)
//
export function* selectEmbeddedThemeSaga() {
    while (true) {
        const { payload: themeId, meta: { form, field } } = yield take(ReduxFormActions.CHANGE);
        if (form === 'theme-asset-properties' && field === 'themeId') {
            const asset: Asset = yield select(getActiveAsset);
            const assetId = asset && asset.id;
            if (assetId) {
                const theme: Theme = yield select(getTheme, { themeId });
                if (theme) {
                    const { width, height } = theme;
                    yield put(setAssetSize({ assetId, width, height }));
                }
                else {
                    yield put(setAssetSize({ assetId, width: 0, height: 0 }));
                }
            }
        }
    }
}
